import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Ban, Megaphone, LayoutList, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchAdvertisers,
  setAdvertiserStatus,
  ADS_STR as S,
  type AdvertiserRow,
} from './adsApi'

/**
 * A20.01 — Рекламодатели (advertisers list). Dormant until `/admin/ads/
 * advertisers` ships; renders the empty state meanwhile. Placeholder permission
 * USERS_VIEW (REGISTER.md: swap for `ads.advertiser.view`).
 */
export function AdvertisersScreen() {
  const { toast } = useToast()
  const columns = useMemo<AdminColumn<AdvertiserRow>[]>(
    () => [
      { key: 'name', header: S.advertiser, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'balance', header: S.balance, type: 'number' },
      { key: 'spent', header: S.spent, type: 'number' },
      { key: 'campaigns', header: S.campaigns, type: 'number' },
      { key: 'created_at', header: S.created, type: 'text' },
    ],
    [],
  )
  const changeStatus = useCallback(
    async (row: AdvertiserRow, status: string) => {
      try {
        await setAdvertiserStatus(row.id, status)
        toast(S.statusChanged, { tone: 'success' })
      } catch {
        toast(S.moderateFailed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<AdvertiserRow>[]>(
    () => [
      {
        id: 'suspend',
        label: S.suspend,
        icon: <Ban size={14} />,
        danger: true,
        onSelect: (r) => changeStatus(r, 'suspended'),
        visible: (r) => r.status === 'active',
      },
      {
        id: 'activate',
        label: S.activate,
        icon: <ShieldCheck size={14} />,
        onSelect: (r) => changeStatus(r, 'active'),
        visible: (r) => r.status !== 'active',
      },
    ],
    [changeStatus],
  )

  const fetcher = useCallback(fetchAdvertisers, [])
  return (
    <>
      <PageHeader
        title={S.advertisers}
        screenId="A20.01"
        actions={
          <Link
            to="/ads/campaigns"
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            <LayoutList size={13} />
            {S.campaigns}
          </Link>
        }
      />
      <DataTable<AdvertiserRow>
        screenId="A20.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Megaphone size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
